import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dna, Cpu, ArrowRight, ShieldCheck, Store } from "lucide-react";
import { api } from "@/lib/api";

const FILTERS = [
  { key: "", label: "All brands", Icon: Store },
  { key: "dx", label: "Diagnostics (DX)", Icon: Dna },
  { key: "medtech", label: "Medtech", Icon: Cpu },
];

function BrandCard({ b }) {
  const Icon = b.category === "dx" ? Dna : Cpu;
  return (
    <Link to={`/marketplace/${b.id}`} data-testid={`brand-card-${b.id}`} className="clinical-card overflow-hidden hover:shadow-lg transition-shadow group">
      <div className="h-28 bg-gradient-to-br from-teal-800 to-slate-900 relative overflow-hidden">
        {b.banner && <img src={b.banner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <span className={`absolute top-3 left-3 badge-pill ${b.category === "dx" ? "bg-teal-50 text-teal-800 border-teal-200" : "bg-indigo-50 text-indigo-800 border-indigo-200"}`}>
          <Icon className="w-3.5 h-3.5" /> {b.category === "dx" ? "Diagnostics" : "Medtech"}
        </span>
      </div>
      <div className="p-6 -mt-8 relative">
        <span className="w-14 h-14 rounded-2xl bg-white shadow ring-1 ring-slate-100 grid place-items-center text-teal-800 font-heading font-bold text-xl">{b.name?.[0]}</span>
        <p className="font-heading font-bold text-lg text-slate-900 mt-3">{b.name}</p>
        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{b.tagline}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-slate-500">{b.product_count} {b.product_count === 1 ? "offering" : "offerings"}{b.city ? ` · ${b.city}` : ""}</span>
          <span className="text-sm font-semibold text-teal-700 flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight className="w-4 h-4" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function MarketplacePage() {
  const [brands, setBrands] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get(`/marketplace/brands${filter ? `?category=${filter}` : ""}`).then((r) => setBrands(r.data)); }, [filter]);
  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div>
        <p className="eyebrow">Partner marketplace</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Diagnostics & Medtech brands</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">Explore verified DX labs and medtech partners. Clinicians can recommend a brand's test or device to a patient directly from a brand page.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="marketplace-filters">
        {FILTERS.map(({ key, label, Icon }) => (
          <button key={key} data-testid={`filter-${key || "all"}`} onClick={() => setFilter(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${filter === key ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-4 flex gap-3 text-sm text-teal-900" data-testid="marketplace-notice">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" /> Recommendations are decision-support only. A clinician assigns tests and reviews all results before anything reaches a patient.
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="brands-grid">
        {brands.length === 0 && <p className="text-sm text-slate-500">No brands yet.</p>}
        {brands.map((b) => <BrandCard key={b.id} b={b} />)}
      </div>
    </div>
  );
}
